import React, { useEffect, useState, useRef } from "react";
import { X, ArrowRight, ArrowLeft, HandWaving } from "@phosphor-icons/react";

/**
 * Tour d'onboarding mobile-first pour les nouvelles téléprospectrices.
 *
 * Affiche un spotlight semi-transparent qui met en évidence un élément
 * (via son data-testid) et une "carte" explicative en dessous. À la 1re
 * visite du portail seulement (flag localStorage `mc68_onboarding_done`).
 *
 * Props :
 *   - steps : [{ testid, title, body, placement? }]
 *   - onDone : callback appelé quand fini ou skippé
 *   - storageKey : clé localStorage pour ne plus le montrer (défaut mc68_onboarding_v1)
 */
const CARD_MARGIN = 12;

export default function OnboardingTour({ steps, onDone, storageKey = "mc68_onboarding_v1" }) {
  const [idx, setIdx] = useState(0);
  const [rect, setRect] = useState(null);
  const cardRef = useRef(null);

  // Skip entirely if user already done it before.
  const [enabled, setEnabled] = useState(() => {
    try { return !localStorage.getItem(storageKey); } catch { return true; }
  });

  const finish = () => {
    try { localStorage.setItem(storageKey, "1"); } catch { /* ignore */ }
    setEnabled(false);
    onDone && onDone();
  };

  // Locate the target element and compute the spotlight rect.
  useEffect(() => {
    if (!enabled || !steps || !steps[idx]) return;
    const target = document.querySelector(`[data-testid="${steps[idx].testid}"]`);
    if (!target) {
      // Element not found — skip to next
      setRect(null);
      return;
    }
    // Smooth scroll into view first
    target.scrollIntoView({ behavior: "smooth", block: "center" });
    const observe = () => {
      const r = target.getBoundingClientRect();
      setRect({ top: r.top, left: r.left, width: r.width, height: r.height });
    };
    setTimeout(observe, 350);
    window.addEventListener("resize", observe);
    window.addEventListener("scroll", observe, true);
    return () => {
      window.removeEventListener("resize", observe);
      window.removeEventListener("scroll", observe, true);
    };
  }, [idx, enabled, steps]);

  if (!enabled || !steps || steps.length === 0) return null;
  const step = steps[idx];
  if (!step) return null;

  // Compute where to place the info card. Prefer BELOW the target unless too low.
  const viewportH = typeof window !== "undefined" ? window.innerHeight : 800;
  const cardHeight = 220;
  let cardTop = null;
  let cardBelow = true;
  if (rect) {
    if (rect.top + rect.height + cardHeight + CARD_MARGIN > viewportH) {
      cardTop = Math.max(CARD_MARGIN, rect.top - cardHeight - CARD_MARGIN);
      cardBelow = false;
    } else {
      cardTop = rect.top + rect.height + CARD_MARGIN;
      cardBelow = true;
    }
  }

  const isFirst = idx === 0;
  const isLast = idx === steps.length - 1;

  return (
    <div className="fixed inset-0 z-[9998]" data-testid="onboarding-tour" aria-label="Découverte guidée">
      {/* Dark overlay with spotlight cutout */}
      {rect ? (
        <svg width="100%" height="100%" className="absolute inset-0 pointer-events-auto" onClick={() => { /* swallow */ }}>
          <defs>
            <mask id="spotlight-mask">
              <rect width="100%" height="100%" fill="white"/>
              <rect
                x={rect.left - 6}
                y={rect.top - 6}
                width={rect.width + 12}
                height={rect.height + 12}
                rx="10"
                fill="black"
              />
            </mask>
          </defs>
          <rect
            width="100%"
            height="100%"
            fill="rgba(19, 78, 52, 0.72)"
            mask="url(#spotlight-mask)"
          />
        </svg>
      ) : (
        <div className="absolute inset-0 bg-primary/70"/>
      )}

      {/* Info card */}
      <div
        ref={cardRef}
        className="absolute left-1/2 -translate-x-1/2 w-[92vw] max-w-md bg-card border-2 border-primary rounded-2xl shadow-2xl p-4 sm:p-5"
        style={{ top: cardTop != null ? cardTop : "50%" }}
        data-testid={`onboarding-card-${idx}`}
      >
        <div className="flex items-start justify-between gap-2 mb-2">
          <div className="flex items-center gap-2">
            <HandWaving size={18} weight="fill" className="text-primary"/>
            <div className="text-[10px] uppercase tracking-widest font-bold text-primary">
              Étape {idx + 1} sur {steps.length}
            </div>
          </div>
          <button
            type="button"
            onClick={finish}
            className="text-muted-foreground hover:text-foreground -mt-1 -mr-1"
            aria-label="Fermer le tour"
            data-testid="onboarding-skip"
            title="Passer le tour"
          >
            <X size={16} weight="bold"/>
          </button>
        </div>

        <h3 className="font-display font-semibold text-lg leading-tight mb-2 text-foreground">
          {step.title}
        </h3>
        <p className="text-sm leading-relaxed text-foreground/85 mb-4">
          {step.body}
        </p>

        {/* Progress dots */}
        <div className="flex items-center gap-1 mb-4" role="tablist">
          {steps.map((_, i) => (
            <span
              key={i}
              className={`h-1.5 rounded-full transition-all ${i === idx ? "bg-primary w-6" : i < idx ? "bg-primary/50 w-3" : "bg-muted w-3"}`}
              aria-hidden="true"
            />
          ))}
        </div>

        {/* Nav */}
        <div className="flex items-center justify-between gap-2">
          <button
            type="button"
            onClick={() => setIdx((v) => Math.max(0, v - 1))}
            disabled={isFirst}
            className="text-xs uppercase tracking-wider font-bold text-muted-foreground disabled:opacity-30 disabled:cursor-not-allowed hover:text-foreground px-2 py-2 inline-flex items-center gap-1"
            data-testid="onboarding-prev"
          >
            <ArrowLeft size={12} weight="bold"/> Retour
          </button>
          {!isLast ? (
            <button
              type="button"
              onClick={() => setIdx((v) => Math.min(steps.length - 1, v + 1))}
              className="bg-primary text-primary-foreground hover:bg-[#0f3d29] hover:text-white px-4 py-2.5 rounded-xl text-sm font-bold inline-flex items-center gap-2"
              data-testid="onboarding-next"
            >
              Suivant <ArrowRight size={13} weight="bold"/>
            </button>
          ) : (
            <button
              type="button"
              onClick={finish}
              className="bg-primary text-primary-foreground hover:bg-[#0f3d29] hover:text-white px-4 py-2.5 rounded-xl text-sm font-bold inline-flex items-center gap-2"
              data-testid="onboarding-finish"
            >
              C&apos;est parti ! 🚀
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
